import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { UserFeaturePreviews } from '@eversports/react-app-base/user-feature-previews'
import useTrackExperiments from '@eversports/react-app-base/use-track-experiments'

import useCart from '../../../data/useCart'
import useCheckoutContext from '../../../data/useCheckoutContext'
import useMutationWithErrorHandling from '../../../data/useMutationWithErrorHandling'
import getPhoenixUrlParams from '../../../lib/get-phoenix-url-params'
import { MultiAnswerPatronQuestionInitialValues, PatronQuestionType } from '../../../typings/AdditionalInformation'
import SkeletonMultiAnswerPatronQuestion from '../Skeletons/SkeletonMultiAnswerPatronQuestion'

import {
  MultiAnswerPatronQuestionCartQuery,
  MultiAnswerPatronQuestionSetMultiAnswerPatronQuestionOnCartMutation,
  MultiAnswerPatronQuestionSetMultiAnswerPatronQuestionOnCartMutationVariables,
  MultiAnswerPatronQuestionCartDocument,
  MultiAnswerPatronQuestionSetMultiAnswerPatronQuestionOnCartDocument,
} from '../../../graphql'
import useEnabledFeaturePreviews from '../../../data/useEnabledFeaturePreviews'
import MultiAnswerPatronQuestionComponent from './MultiAnswerPatronQuestionComponent'
import getOrderedPatronageAnswers, { getRandomizedPatronageAnswer } from './patronage-answer-utils'

const MultiAnswerPatronQuestion = () => {
  const location = useLocation()
  const context = useCheckoutContext()
  const enabledFeaturePreviews = useEnabledFeaturePreviews()
  const { cartId, origin } = getPhoenixUrlParams(location.search)
  const isRandomizedPatronQuestionEnabled = Boolean(
    enabledFeaturePreviews?.includes(UserFeaturePreviews.RANDOMIZED_PATRON_QUESTION),
  )

  useTrackExperiments(
    [UserFeaturePreviews.RANDOMIZED_PATRON_QUESTION],
    enabledFeaturePreviews as Array<UserFeaturePreviews> | undefined,
  )

  const patronageAnswers = useMemo(
    () => (isRandomizedPatronQuestionEnabled ? getRandomizedPatronageAnswer() : getOrderedPatronageAnswers(origin)),
    [enabledFeaturePreviews],
  )

  const [initialValues, setInitialValues] = useState<MultiAnswerPatronQuestionInitialValues>()
  const { cartData, isCartLoading } = useCart<MultiAnswerPatronQuestionCartQuery>(MultiAnswerPatronQuestionCartDocument)

  const [setMultiAnswerPatronQuestionOnCart] = useMutationWithErrorHandling<
    MultiAnswerPatronQuestionSetMultiAnswerPatronQuestionOnCartMutation,
    MultiAnswerPatronQuestionSetMultiAnswerPatronQuestionOnCartMutationVariables
  >(MultiAnswerPatronQuestionSetMultiAnswerPatronQuestionOnCartDocument, {})

  useEffect(() => {
    if (!cartData) return

    const multiAnswerPatronQuestionInitialValue =
      cartData.cart.multiAnswerPatronQuestionInput !== null ? cartData.cart.multiAnswerPatronQuestionInput : undefined
    setInitialValues({ patronageAnswer: multiAnswerPatronQuestionInitialValue })
  }, [cartData])

  if (!cartData || isCartLoading || !initialValues) {
    return <SkeletonMultiAnswerPatronQuestion />
  }

  // Render nothing if there should not be a multi answer patron question
  if (cartData.cart.patronQuestionType !== PatronQuestionType.PATRON_QUESTION_TYPE_CHANNEL) {
    context.setIsMultiAnswerPatronQuestionValid(true)
    return null
  }

  const { venue } = cartData.cart

  const handleChange = ({ patronageAnswer }: MultiAnswerPatronQuestionInitialValues) => {
    if (patronageAnswer === undefined) return

    // eslint-disable-next-line  @typescript-eslint/no-floating-promises
    setMultiAnswerPatronQuestionOnCart({ variables: { cartId: cartId as string, patronageAnswer } })
    context.setIsMultiAnswerPatronQuestionValid(true)
  }

  return (
    <MultiAnswerPatronQuestionComponent
      initialValues={initialValues}
      venueName={venue.name}
      patronageAnswers={patronageAnswers}
      handleChange={handleChange}
    />
  )
}

export default MultiAnswerPatronQuestion
